/**
 * The plan toggle (mission 9 WP12 T074; sheet 08 callout 3; B.1, B.2; FR-023, C-010; squad U25;
 * contracts/ui-api.md "Sheet 08", "Accessibility").
 *
 * A radiogroup of native radios (one `name`), so the arrow keys move the choice and only the chosen radio
 * is in the tab order (style guide §9). Each label is the register's plan sentence with the price from the
 * billing read's `prices` through `money()`, the price in mono (CLAUDE.md §5). The annual option states its
 * allowance per monthly cycle, not the year's total up front (`planAnnualAllowance`, B.2). Every figure is
 * an argument from `GET /v1/billing`, never a literal (C-010).
 */
import { useId, type ReactNode } from 'react'

import type { PlanId, PlanPrices } from '@/lib/api/billing'
import { Mono, money } from '@/lib/format'
import { PROVISIONAL_COPY } from '@/lib/provisional-copy'

/**
 * The annual plan's monthly cycles: a calendar fact, not a price or a limit. It only names the year's total
 * the allowance is not granted as (B.2).
 */
const CYCLES_PER_YEAR = 12

/** `text` with its first `price` in mono (CLAUDE.md §5); the sentence stays the register's. */
export function withMonoPrice(text: string, price: string): ReactNode {
  const at = text.indexOf(price)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <Mono>{price}</Mono>
      {text.slice(at + price.length)}
    </>
  )
}

export interface PlanToggleProps {
  prices: PlanPrices
  /** `terms.allowance_per_cycle`. */
  allowance: number
  plan: PlanId
  onPlanChange: (plan: PlanId) => void
  /** The id of the heading that names the group. */
  labelledBy: string
}

export function PlanToggle({ prices, allowance, plan, onPlanChange, labelledBy }: PlanToggleProps) {
  const name = useId()
  const monthly = money(prices.monthly_cents)
  const annual = money(prices.annual_cents)
  const options: { id: PlanId; label: ReactNode; note?: string }[] = [
    { id: 'monthly', label: withMonoPrice(PROVISIONAL_COPY.planMonthly.render({ price: monthly, interval: 'month' }), monthly) },
    {
      id: 'annual',
      label: withMonoPrice(PROVISIONAL_COPY.planAnnual.render({ price: annual, interval: 'year' }), annual),
      note: PROVISIONAL_COPY.planAnnualAllowance.render({ allowance, yearTotal: allowance * CYCLES_PER_YEAR }),
    },
  ]
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="flex flex-col gap-3">
      {options.map((option) => (
        <div key={option.id} className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-body text-navy-950">
            <input
              type="radio"
              name={name}
              value={option.id}
              className="size-[18px] accent-navy-950"
              checked={plan === option.id}
              onChange={() => onPlanChange(option.id)}
            />
            <span>{option.label}</span>
          </label>
          {option.note === undefined ? null : <p className="pl-7 text-body-sm text-navy-600">{option.note}</p>}
        </div>
      ))}
    </div>
  )
}
